import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServiceCenter } from "../contexts";
import { ServiceCenterAPI } from "../services";
import { normalizePhone, formatLastMessagePreview, formatRelativeDate } from "../utils/formatters";
import { CONVERSATION_FILTERS, PAGINATION, SERVICE_STATUS } from "../utils/constants";
import { contactToConversation } from "../utils/conversationAdapter";

// Module-level lock: useConversations() é chamado em múltiplos componentes
// (ServiceCenter + ConversationSidebar). Sem isso, cada instância dispara
// seu próprio loadConversations() na montagem → request duplicado.
// O lock garante que só 1 chamada acontece por window de 200ms.
const _loadLock = { lastKey: null, lastTs: 0 };
const _shouldLoad = (key) => {
  const now = Date.now();
  if (_loadLock.lastKey === key && now - _loadLock.lastTs < 200) {
    return false;
  }
  _loadLock.lastKey = key;
  _loadLock.lastTs = now;
  return true;
};

/**
 * Hook para gerenciar conversas
 */
const useConversations = () => {
  const {
    selectedAccount,
    accounts,
    selectedChannels,
    conversations,
    conversationsOffset,
    hasMoreConversations,
    selectedConversation,
    filters,
    loading,
    unreadCounts,
    setConversations,
    addConversations,
    updateConversation,
    setSelectedConversation,
    setLoading,
    resetUnreadCount,
    setSelectedContact,
    setFilters,
  } = useServiceCenter();

  /**
   * Carrega conversas iniciais (multi-canal omnichannel).
   * Mescla conversas de todas as accounts ativas, filtrando por
   * `selectedChannels` (vazio = todos).
   */
  const loadConversations = useCallback(async () => {
    if (!accounts || accounts.length === 0) return;

    setLoading({ conversations: true });

    try {
      const response = await ServiceCenterAPI.getMultiChannelConversations({
        channels: selectedChannels,
        limit: PAGINATION.CONVERSATIONS_PER_PAGE,
        offset: 0,
      });

      const convs = response.conversations || response || [];
      const responseCount = response.count ?? convs.length;
      const responseLimit = response.limit ?? PAGINATION.CONVERSATIONS_PER_PAGE;

      const formattedConversations = convs.map((chat) => ({
        id: chat.id,
        channel: chat.channel || "whatsapp",
        channel_user_id: chat.channel_user_id || chat.phone || chat.phone_raw,
        provider_account_id: chat.provider_account_id || null,
        provider_identity: chat.provider_identity || null,
        phone: chat.phone || chat.phone_raw || chat.channel_user_id,
        phone_raw: chat.phone_raw || chat.channel_user_id,
        name: chat.name || chat.phone || chat.channel_user_id,
        avatar:
          chat.picture ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(
            chat.name || chat.phone || chat.channel_user_id || ""
          )}&background=random`,
        lastMessage: chat.lastMessage?.body || chat.lastMessage?.text || "",
        lastMessageTime: chat.lastMessage?.timestamp
          ? formatRelativeDate(chat.lastMessage.timestamp * 1000)
          : "",
        lastMessageTimestamp: chat.lastMessage?.timestamp || 0,
        fromMe: chat.lastMessage?.fromMe || chat.lastMessage?.from_me || false,
        // Dados de atendimento
        contact_id: chat.contact_id,
        whatsapp_id: chat.whatsapp_id,
        whatsapp_lid: chat.whatsapp_lid,
        instagram_id: chat.channel === "instagram" ? chat.channel_user_id : chat.instagram_id,
        service_status: chat.service_status || SERVICE_STATUS.NEW,
        service_status_display: chat.service_status_display || "Novo",
        current_attendant_id: chat.current_attendant_id,
        current_attendant_name: chat.current_attendant_name,
        current_department_id: chat.current_department_id,
        current_department_name: chat.current_department_name,
        tags: chat.tags,
        bot_status: chat.bot_status ?? 1,
        source: chat.source || chat.channel || "whatsapp",
        temperature: chat.temperature || null,
        priority: chat.priority || null,
        deal_value: chat.deal_value || null,
        messages: [],
      }));

      setConversations(
        formattedConversations,
        formattedConversations.length,
        responseCount >= responseLimit
      );
    } catch (error) {
      console.error("Erro ao carregar conversas:", error);
    } finally {
      setLoading({ conversations: false });
    }
  }, [accounts, selectedChannels, setConversations, setLoading]);

  /**
   * Carrega mais conversas (paginação multi-canal)
   */
  const loadMoreConversations = useCallback(async () => {
    if (!accounts?.length || !hasMoreConversations || loading.conversations) {
      return;
    }

    setLoading({ conversations: true });

    try {
      const response = await ServiceCenterAPI.getMultiChannelConversations({
        channels: selectedChannels,
        limit: PAGINATION.CONVERSATIONS_PER_PAGE,
        offset: conversationsOffset,
      });

      const convs = response.conversations || response || [];
      const responseCount = response.count ?? convs.length;
      const responseLimit = response.limit ?? PAGINATION.CONVERSATIONS_PER_PAGE;

      if (convs.length > 0) {
        const formattedConversations = convs.map((chat) => ({
          id: chat.id,
          channel: chat.channel || "whatsapp",
          channel_user_id: chat.channel_user_id || chat.phone || chat.phone_raw,
          provider_account_id: chat.provider_account_id || null,
          provider_identity: chat.provider_identity || null,
          phone: chat.phone || chat.phone_raw || chat.channel_user_id,
          phone_raw: chat.phone_raw || chat.channel_user_id,
          name: chat.name || chat.phone || chat.channel_user_id,
          avatar:
            chat.picture ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              chat.name || chat.phone || chat.channel_user_id || ""
            )}&background=random`,
          lastMessage: chat.lastMessage?.body || chat.lastMessage?.text || "",
          lastMessageTime: chat.lastMessage?.timestamp
            ? formatRelativeDate(chat.lastMessage.timestamp * 1000)
            : "",
          lastMessageTimestamp: chat.lastMessage?.timestamp || 0,
          fromMe: chat.lastMessage?.fromMe || chat.lastMessage?.from_me || false,
          contact_id: chat.contact_id,
          whatsapp_id: chat.whatsapp_id,
          instagram_id: chat.channel === "instagram" ? chat.channel_user_id : chat.instagram_id,
          service_status: chat.service_status || SERVICE_STATUS.NEW,
          service_status_display: chat.service_status_display || "Novo",
          current_attendant_id: chat.current_attendant_id,
          current_attendant_name: chat.current_attendant_name,
          current_department_id: chat.current_department_id,
          current_department_name: chat.current_department_name,
          tags: chat.tags,
          bot_status: chat.bot_status ?? 1,
          source: chat.source || chat.channel || "whatsapp",
          temperature: chat.temperature || null,
          priority: chat.priority || null,
          deal_value: chat.deal_value || null,
          messages: [],
        }));

        addConversations(
          formattedConversations,
          conversationsOffset + formattedConversations.length,
          responseCount >= responseLimit
        );
      } else {
        addConversations([], conversationsOffset, false);
      }
    } catch (error) {
      console.error("Erro ao carregar mais conversas:", error);
    } finally {
      setLoading({ conversations: false });
    }
  }, [
    accounts,
    selectedChannels,
    hasMoreConversations,
    loading.conversations,
    conversationsOffset,
    addConversations,
    setLoading,
  ]);

  // Token de cancelamento pra `selectConversation`. Se o usuário clicar
  // rápido em A → B enquanto o getBotStatus de A está pendente, o token
  // é incrementado e o resultado de A é descartado quando voltar.
  const selectionTokenRef = useRef(0);

  /**
   * Seleciona uma conversa.
   *
   * Não muta o objeto recebido — clona pra aplicar bot_status e evitar
   * surpresas em quem mantém referência (sidebar, searchResults).
   *
   * Cancela seleção concorrente: se outra `selectConversation` rodar
   * antes desta resolver, descarta o resultado pra evitar stale write.
   */
  const selectConversation = useCallback(
    async (conversation) => {
      // Evitar re-seleção
      if (selectedConversation?.id === conversation.id) {
        return;
      }

      const myToken = ++selectionTokenRef.current;
      const phone = normalizePhone(conversation.phone);

      // Atualiza imediatamente com o que temos. UX responsiva — o
      // bot_status é refinado depois quando o getBotStatus resolver.
      // Clona pra não mutar a prop original (evita propagar mudanças
      // implícitas na sidebar / searchResults).
      const initialClone = { ...conversation };
      setSelectedConversation(initialClone);
      resetUnreadCount(phone);
      setSelectedContact(null);

      // Refina bot_status em background. Se o token mudou, descarta.
      try {
        const botStatus = await ServiceCenterAPI.getBotStatus(phone);
        if (selectionTokenRef.current !== myToken) return; // stale
        if (botStatus?.success) {
          setSelectedConversation({
            ...initialClone,
            bot_status: botStatus.data?.bot_status ?? 1,
          });
        }
      } catch (error) {
        if (selectionTokenRef.current !== myToken) return;
        console.error("Erro ao buscar status do bot:", error);
      }
    },
    [selectedConversation?.id, setSelectedConversation, resetUnreadCount, setSelectedContact]
  );

  /**
   * Atualiza conversa com nova mensagem
   */
  const updateConversationWithMessage = useCallback(
    (phone, message) => {
      const normalizedPhone = normalizePhone(phone);

      const existingConv = conversations.find(
        (conv) => normalizePhone(conv.phone) === normalizedPhone
      );

      if (existingConv) {
        updateConversation({
          ...existingConv,
          lastMessage: formatLastMessagePreview(message.text),
          lastMessageTime: message.time,
          lastMessageTimestamp: message.timestamp,
        });
      } else {
        // Nova conversa - adicionar no início
        const newConv = {
          id: normalizedPhone,
          phone: normalizedPhone,
          name: normalizedPhone,
          avatar: `https://ui-avatars.com/api/?name=${normalizedPhone}&background=random`,
          lastMessage: formatLastMessagePreview(message.text),
          lastMessageTime: message.time,
          lastMessageTimestamp: message.timestamp,
          fromMe: message.isMe,
          service_status: SERVICE_STATUS.NEW,
          bot_status: 1,
          messages: [],
        };

        setConversations([newConv, ...conversations], conversationsOffset + 1);
      }
    },
    [conversations, conversationsOffset, updateConversation, setConversations]
  );

  // ==========================================
  // BUSCA SERVER-SIDE (Camada 1 do híbrido)
  // ==========================================
  // Estado independente do filtro client-side: o filtro client-side roda
  // enquanto o usuário digita (sobre os 20 carregados); a busca server-side
  // dispara explicitamente (lupa ou Enter) e busca em todo o histórico do
  // account via /contacts/service/search/.
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const searchContactsServer = useCallback(async (query) => {
    const trimmed = String(query || "").trim();
    if (trimmed.length < 2) {
      return { results: [], count: 0 };
    }
    setIsSearching(true);
    setSearchActive(true);
    setSearchQuery(trimmed);
    try {
      const data = await ServiceCenterAPI.searchContacts(trimmed);
      const conversations = (data.results || []).map(contactToConversation);
      setSearchResults(conversations);
      return { results: conversations, count: data.count ?? conversations.length };
    } catch (error) {
      console.error("Erro ao buscar contatos:", error);
      setSearchResults([]);
      return { results: [], count: 0 };
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchActive(false);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  /**
   * Filtra conversas baseado nos filtros ativos
   */
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    // Filtro de busca
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (conv) =>
          conv.name?.toLowerCase().includes(searchLower) ||
          conv.phone?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro de status
    if (filters.status !== CONVERSATION_FILTERS.STATUS.ALL) {
      filtered = filtered.filter(
        (conv) => conv.service_status === filters.status
      );
    }

    // Filtro de departamento
    if (filters.department) {
      filtered = filtered.filter(
        (conv) => conv.current_department_id === filters.department
      );
    }

    // Ordenar por última mensagem (mais recente primeiro)
    filtered.sort(
      (a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0)
    );

    return filtered;
  }, [conversations, filters]);

  /**
   * Agrupa conversas por status
   */
  const groupedConversations = useMemo(() => {
    const groups = {
      [SERVICE_STATUS.NEW]: [],
      [SERVICE_STATUS.IN_SERVICE]: [],
      [SERVICE_STATUS.WAITING]: [],
      [SERVICE_STATUS.FINISHED]: [],
    };

    filteredConversations.forEach((conv) => {
      const status = conv.service_status || SERVICE_STATUS.NEW;
      if (groups[status]) {
        groups[status].push(conv);
      }
    });

    return groups;
  }, [filteredConversations]);

  /**
   * Contadores por status
   */
  const statusCounts = useMemo(() => {
    return {
      [SERVICE_STATUS.NEW]: groupedConversations[SERVICE_STATUS.NEW].length,
      [SERVICE_STATUS.IN_SERVICE]: groupedConversations[SERVICE_STATUS.IN_SERVICE].length,
      [SERVICE_STATUS.WAITING]: groupedConversations[SERVICE_STATUS.WAITING].length,
      [SERVICE_STATUS.FINISHED]: groupedConversations[SERVICE_STATUS.FINISHED].length,
      total: filteredConversations.length,
    };
  }, [groupedConversations, filteredConversations]);

  /**
   * Atualiza filtro de busca
   */
  const setSearchFilter = useCallback(
    (search) => {
      setFilters({ search });
    },
    [setFilters]
  );

  /**
   * Atualiza filtro de status
   */
  const setStatusFilter = useCallback(
    (status) => {
      setFilters({ status });
    },
    [setFilters]
  );

  /**
   * Atualiza filtro de departamento
   */
  const setDepartmentFilter = useCallback(
    (department) => {
      setFilters({ department });
    },
    [setFilters]
  );

  // Ref pra função atual — evita re-disparo do effect só porque a closure
  // de loadConversations muda quando accounts/selectedChannels mudam.
  const loadConversationsRef = useRef(loadConversations);
  useEffect(() => {
    loadConversationsRef.current = loadConversations;
  }, [loadConversations]);

  // Carrega conversas quando o usuário entra na Central OU troca filtro.
  // Lock module-level previne duplicação quando o hook é instanciado em
  // múltiplos componentes (ServiceCenter + ConversationSidebar).
  useEffect(() => {
    if (!accounts || accounts.length === 0) return;
    const channelsKey = selectedChannels
      ? [...selectedChannels].sort().join(",")
      : "all";
    const lockKey = `${accounts.length}|${channelsKey}`;
    if (_shouldLoad(lockKey)) {
      loadConversationsRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts?.length, selectedChannels]);

  // Listener para transferências recebidas - atualiza a lista automaticamente
  useEffect(() => {
    const handleTransferNotification = (event) => {
      if (event?.data?.newTransfer) {
        loadConversationsRef.current();
      }
    };
    window.addEventListener("message", handleTransferNotification);
    return () => {
      window.removeEventListener("message", handleTransferNotification);
    };
  }, []);

  // Listener para mensagens novas — se chegar de um telefone que ainda
  // não está na lista local, dispara loadConversations() para trazer a
  // nova conversa do backend. Debounce 500ms para coalescer múltiplas
  // mensagens em sequência.
  const unknownPhoneTimerRef = useRef(null);
  useEffect(() => {
    const handleNewMessageForUnknown = (event) => {
      const incoming = event?.data?.newWaMessage || event?.data?.newWaSentMessage;
      if (!incoming) return;

      // Qual o telefone do chat? Se recebida, é `from`; se enviada, é `to`.
      const chatPhoneRaw = incoming.from_me ? incoming.to : incoming.from;
      const chatPhone = normalizePhone(chatPhoneRaw);
      if (!chatPhone) return;

      // Filtrar: só age se for para a conta selecionada
      const ourPhone = normalizePhone(
        incoming.from_me ? incoming.from : incoming.to
      );
      if (selectedAccount?.phone &&
          normalizePhone(selectedAccount.phone) !== ourPhone) {
        return;
      }

      // Conversa já existe localmente? useWebSocket já cuida do preview.
      const existing = conversations.find(
        (c) => normalizePhone(c.phone) === chatPhone
      );
      if (existing) return;

      // Phone desconhecido → debounce para coalescer múltiplas mensagens
      if (unknownPhoneTimerRef.current) {
        clearTimeout(unknownPhoneTimerRef.current);
      }
      unknownPhoneTimerRef.current = setTimeout(() => {
        loadConversationsRef.current();
      }, 500);
    };

    window.addEventListener("message", handleNewMessageForUnknown);

    return () => {
      window.removeEventListener("message", handleNewMessageForUnknown);
      if (unknownPhoneTimerRef.current) {
        clearTimeout(unknownPhoneTimerRef.current);
      }
    };
  }, [conversations, selectedAccount?.phone]);

  // Listener para enriquecimento async de Contact (Messenger/Instagram).
  // Backend dispara via WS após buscar name/picture_url da Graph API.
  // Frontend atualiza in-place o item da lista sem refetch.
  useEffect(() => {
    const handleContactUpdated = (event) => {
      const upd = event?.data?.contactUpdated;
      if (!upd || !upd.channel_user_id) return;

      const target = conversations.find(
        (c) =>
          c.channel === upd.channel
          && (c.channel_user_id === upd.channel_user_id
              || c.phone === upd.channel_user_id),
      );
      if (!target) return;

      updateConversation({
        ...target,
        name: upd.name || target.name,
        avatar: upd.picture_url || target.avatar,
        picture_url: upd.picture_url || target.picture_url,
      });
    };

    window.addEventListener("message", handleContactUpdated);
    return () => window.removeEventListener("message", handleContactUpdated);
  }, [conversations, updateConversation]);

  return {
    // State
    conversations: filteredConversations,
    groupedConversations,
    statusCounts,
    selectedConversation,
    hasMoreConversations,
    isLoading: loading.conversations,
    filters,

    // Search server-side (Camada 1)
    searchResults,
    isSearching,
    searchActive,
    searchQuery,

    // Actions
    loadConversations,
    loadMoreConversations,
    selectConversation,
    updateConversationWithMessage,
    setSearchFilter,
    setStatusFilter,
    setDepartmentFilter,
    searchContactsServer,
    clearSearch,
  };
};

export default useConversations;
